<template>
  <div class="content">
    <card class="text-left">
      <template slot="header">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h4 class="card-title">투표 관리</h4>
            <p class="card-category">
              정렬: 설정 필요 → 시작 임박 예정 → 종료 임박 진행중 → 준비 → 종료/취소. 상태는 일정 기반 결과이고, 노출은 별도 운영 스위치입니다.
            </p>
          </div>
          <base-button @click="goCreate" type="primary" size="sm">+ 투표 생성</base-button>
        </div>
      </template>
      <div class="row">
        <div class="col-md-2">
          <label class="col-form-label">타입</label>
          <el-select v-model="type" placeholder="전체" @change="getPolls(1)">
            <el-option v-for="t in typeOptions" :key="t.value" :label="t.label" :value="t.value" />
          </el-select>
        </div>
        <div class="col-md-2">
          <label class="col-form-label">상태</label>
          <el-select v-model="status" placeholder="전체" clearable @change="getPolls(1)">
            <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>
        </div>
        <ArtistSelect
          class="col-md-5"
          :initial-unit-id="0"
          :include-unit-artist="false"
          :show-all-units="true"
          :show-members="false"
          :is-multiple="false"
          @unitChangeEvent="onUnitChange"
        />
        <div class="col-md-2">
          <label class="col-form-label">&nbsp;</label>
          <base-button @click="getPolls(1)" type="default" block>검색</base-button>
        </div>
      </div>
    </card>

    <card class="text-left">
      <div class="row">
        <el-table :data="data" @row-click="rowClicked" style="width: 100%">
          <el-table-column prop="resourceId" label="ID" :min-width="50" />
          <el-table-column label="운영 상태" :min-width="90">
            <template slot-scope="props">
              <span class="funding-badge" :class="operationClass(props.row)">{{ operationLabel(props.row) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="노출" :min-width="70" align="center">
            <template slot-scope="props">
              <span class="funding-badge" :class="props.row.isShow ? 'funding-badge-visible' : 'funding-badge-muted'">
                {{ props.row.isShow ? "노출" : "숨김" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="title" label="제목" :min-width="200" />
          <el-table-column label="대상" :min-width="170">
            <template slot-scope="props">
              <template v-if="props.row.type === PollType.FUNDING">
                <div>{{ props.row.unitName || props.row.artistHomeName || "-" }}</div>
                <small class="text-muted">{{ props.row.memberName ? `멤버: ${props.row.memberName}` : "그룹 전체" }}</small>
              </template>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column label="스케줄" :min-width="120">
            <template slot-scope="props">
              <template v-if="props.row.type === PollType.FUNDING">
                <div v-if="props.row.scheduleId">
                  <button class="btn-link-like" @click.stop="openSchedule(props.row)">#{{ props.row.scheduleId }}</button>
                  <div class="text-muted small">{{ props.row.scheduleName || "스케줄명 미확인" }}</div>
                </div>
                <span v-else class="text-muted">미연동</span>
              </template>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column label="설정" :min-width="130">
            <template slot-scope="props">
              <span :class="setupClass(props.row)">{{ setupLabel(props.row) }}</span>
              <div v-if="props.row.type === PollType.FUNDING" class="text-muted small">
                {{ props.row.voteCountTarget == null ? "목표 미설정" : `${Number(props.row.gemTarget || 0).toLocaleString()}젬` }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="생성" :min-width="64" align="center">
            <template slot-scope="props">
              <span class="funding-badge" :class="props.row.isAutoCreated ? 'funding-badge-auto' : 'funding-badge-muted'">
                {{ props.row.isAutoCreated ? "자동" : "수동" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="예정 공개" :min-width="150">
            <template slot-scope="props">{{ formatPublishedAt(props.row) }}</template>
          </el-table-column>
          <el-table-column label="투표 기간" :min-width="180">
            <template slot-scope="props">{{ formatPeriod(props.row) }}</template>
          </el-table-column>
          <!-- 달성률(현재/서포트 달성률)·cast 현황은 목록에서 제외 — 상세에서 확인(목록은 운영 식별용). 필요 시 복원. -->
        </el-table>
      </div>
      <div class="row">
        <base-pagination class="pagination-no-border" v-model="currentPage" :pageCount="totalPages" />
      </div>
    </card>
  </div>
</template>
<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import ArtistSelect from "@/pages/Components/ArtistSelect.vue";
import { Card, BaseButton, BasePagination } from "@/components/index";
import { formatDateTime } from "@/util/DateUtils";
import { PollType, PollTypeOptions } from "@/common/PollTypes";

const STATUS_LABELS = {
  CREATED: "준비",
  PUBLISHED: "예정",
  WORKING: "진행중",
  FINISHED: "종료",
  CLOSED: "마감",
  CANCELLED: "취소",
};

export default {
  components: {
    Card,
    BaseButton,
    BasePagination,
    ArtistSelect,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  async created() {
    await this.getPolls();
  },
  data() {
    return {
      PollType,
      typeOptions: PollTypeOptions,
      type: PollType.FUNDING,
      statusOptions: Object.keys(STATUS_LABELS).map((value) => ({ value, label: STATUS_LABELS[value] })),
      data: [],
      status: null,
      unitId: null,
      currentPage: 1,
      totalPages: null,
      limit: 20,
    };
  },
  watch: {
    currentPage() {
      this.getPolls(this.currentPage);
    },
  },
  methods: {
    onUnitChange(param) {
      this.unitId = param && param.unitId ? param.unitId : null;
    },
    async getPolls(page) {
      const params = { page: page ? page : 1, limit: this.limit };
      if (this.type) params.type = this.type;
      if (this.status) params.status = this.status;
      if (this.unitId) params.unitId = this.unitId;
      return this.$axios.get("/votes", { params }).then((res) => {
        const payload = res.data.data;
        this.data = payload.data;
        const total = payload.meta ? payload.meta.total : 0;
        this.totalPages = Math.max(1, Math.ceil(total / this.limit));
      });
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    statusClass(status) {
      if (status === "WORKING") return "text-success";
      if (status === "PUBLISHED") return "text-info";
      if (status === "CANCELLED" || status === "CLOSED") return "text-muted";
      return "";
    },
    operationLabel(row) {
      if (this.needsSetup(row)) return "설정 필요";
      if (this.isPublishReserved(row)) return "공개 예약";
      if (!row.isShow) return "숨김";
      if (row.status === "FINISHED" && row.fundingResult === "SUCCESS") return "종료(성공)";
      if (row.status === "FINISHED" && row.fundingResult === "FAIL") return "종료(실패)";
      return this.statusLabel(row.status);
    },
    operationClass(row) {
      if (this.needsSetup(row)) return "funding-badge-attention";
      if (this.isPublishReserved(row)) return "funding-badge-scheduled";
      if (!row.isShow) return "funding-badge-muted";
      if (row.status === "WORKING") return "funding-badge-live";
      if (row.status === "PUBLISHED") return "funding-badge-scheduled";
      if (row.status === "CANCELLED" || row.status === "CLOSED") return "funding-badge-muted";
      return "funding-badge-draft";
    },
    isPublishReserved(row) {
      return row.status === "CREATED" && row.publishedAt && new Date(row.publishedAt).getTime() > Date.now();
    },
    needsSetup(row) {
      const isPreparing = ["CREATED", "PUBLISHED"].includes(row.status);
      if (!isPreparing) return false;
      if (row.type === PollType.FUNDING && Number(row.voteCountTarget || 0) <= 0) return true;
      return Number(row.optionCount || 0) <= 0;
    },
    setupLabel(row) {
      const isPreparing = ["CREATED", "PUBLISHED"].includes(row.status);
      if (row.type === PollType.FUNDING && isPreparing && Number(row.voteCountTarget || 0) <= 0) return "목표 필요";
      if (isPreparing && Number(row.optionCount || 0) <= 0) return "선택지 필요";
      if (row.type === PollType.FUNDING && !row.scheduleId) return "스케줄 미연동";
      return "설정 완료";
    },
    setupClass(row) {
      if (this.needsSetup(row)) return "text-warning";
      if (row.type === PollType.FUNDING && !row.scheduleId) return "text-muted";
      return "text-success";
    },
    formatTarget(row) {
      const target = row.voteCountTarget == null ? "미설정" : Number(row.voteCountTarget).toLocaleString();
      const current = Number(row.voteCount || 0).toLocaleString();
      const rate = row.fundingRatePercent == null ? "" : ` · ${Number(row.fundingRatePercent).toLocaleString()}%`;
      return `${target} / ${current}${rate}`;
    },
    formatPeriod(row) {
      return `${formatDateTime(row.startAt)} ~ ${formatDateTime(row.finishAt)}`;
    },
    formatPublishedAt(row) {
      return row.publishedAt ? formatDateTime(row.publishedAt) : "-";
    },
    formatSchedulePeriod(schedule) {
      return `${formatDateTime(schedule.startTime)} ~ ${formatDateTime(schedule.endTime)}`;
    },
    scheduleOptionLabel(schedule) {
      const target = schedule.members && schedule.members.length > 0 ? ` / 멤버 ${schedule.members.map((m) => m.name).join(", ")}` : " / 그룹";
      return `#${schedule.scheduleId} ${schedule.title}${target} / ${formatDateTime(schedule.startTime)}`;
    },
    rowClicked(row) {
      this.$router.push(`/poll/detail?pollId=${row.resourceId}`);
    },
    openSchedule(row) {
      if (!row.scheduleId || !row.unitId) return;
      window.open(`/schedules/scheduleForm?scheduleId=${row.scheduleId}&unitId=${row.unitId}`);
    },
    goCasts(row) {
      this.$router.push(`/poll/casts?pollId=${row.resourceId}`);
    },
    goCreate() {
      this.$router.push("/poll/detail");
    },
  },
};
</script>
<style scoped>
tr {
  cursor: pointer;
}
.btn-link-like {
  background: transparent;
  border: 0;
  color: #2c7be5;
  cursor: pointer;
  padding: 0;
}
.funding-badge {
  border: 1px solid transparent;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 22px;
  padding: 2px 8px;
  color: #4a5568;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}
.funding-badge-live,
.funding-badge-visible {
  border-color: #b8ddc3;
  background: #eef8f1;
  color: #23653a;
}
.funding-badge-scheduled {
  border-color: #c7d8f3;
  background: #eef4ff;
  color: #315a91;
}
.funding-badge-attention {
  border-color: #efd399;
  background: #fff7e6;
  color: #805b10;
}
.funding-badge-auto {
  border-color: #bedddd;
  background: #edf7f7;
  color: #2d6368;
}
.funding-badge-muted,
.funding-badge-draft {
  border-color: #d8dde6;
  background: #f5f7fa;
  color: #5f6b7a;
}
</style>
